import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import type { MeetingInput, Person, SystemStatus } from "../types";
import { TIMEZONES, defaultStart, initials, toNaiveLocal } from "../components/util";

const DURATIONS = [15, 30, 45, 60, 90, 120];

export function MeetingFormPage({ status }: { status: SystemStatus | null }) {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();

  const [people, setPeople] = useState<Person[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState<MeetingInput>({
    topic: "",
    agenda: "",
    start_time: "",
    duration_min: 30,
    timezone: "Europe/Moscow",
    participant_ids: [],
    auto_recording: "cloud",
    host_email: "",
  });

  // Пояс и режим записи по умолчанию берём из настроек сервера
  useEffect(() => {
    if (!status || editing) return;
    setForm((f) =>
      f.start_time
        ? f
        : {
            ...f,
            timezone: status.defaultTimezone,
            auto_recording: status.autoRecording as MeetingInput["auto_recording"],
            start_time: defaultStart(status.defaultTimezone),
          },
    );
  }, [status, editing]);

  useEffect(() => {
    api.listPeople({}).then(setPeople).catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!editing) return;
    api
      .getMeeting(Number(id))
      .then((m) => {
        setForm({
          topic: m.topic,
          agenda: m.agenda,
          start_time: toNaiveLocal(m.start_time, m.timezone),
          duration_min: m.duration_min,
          timezone: m.timezone,
          participant_ids: m.participants.map((p) => p.id),
          auto_recording: m.auto_recording,
          host_email: m.host_email,
        });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, editing]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return people;
    return people.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q) ||
        p.role.toLowerCase().includes(q) ||
        p.company.toLowerCase().includes(q),
    );
  }, [people, search]);

  const toggle = (personId: number) => {
    setForm((f) => ({
      ...f,
      participant_ids: f.participant_ids.includes(personId)
        ? f.participant_ids.filter((x) => x !== personId)
        : [...f.participant_ids, personId],
    }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const meeting = editing
        ? await api.updateMeeting(Number(id), form)
        : await api.createMeeting(form);
      navigate(`/meetings/${meeting.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="empty">
          <span className="spinner" /> Загружаю встречу…
        </div>
      </div>
    );
  }

  const selected = people.filter((p) => form.participant_ids.includes(p.id));

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{editing ? "Изменить встречу" : "Новая встреча"}</h1>
          <p>
            {status?.zoom.configured
              ? "Встреча заведётся в Zoom, ссылка появится в карточке."
              : "Zoom пока не настроен — встреча сохранится локально и уедет в Zoom, как только появятся ключи."}
          </p>
        </div>
        <Link className="btn" to="/meetings">
          Назад
        </Link>
      </div>

      {error && <div className="banner red">{error}</div>}

      <form onSubmit={submit}>
        <div className="card">
          <div className="card-body">
            <div className="field">
              <label htmlFor="topic">Тема</label>
              <input
                id="topic"
                required
                maxLength={200}
                value={form.topic}
                placeholder="Например: еженедельная синхронизация по проекту"
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
              />
            </div>

            <div className="grid-3">
              <div className="field">
                <label htmlFor="start">Дата и время</label>
                <input
                  id="start"
                  type="datetime-local"
                  required
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="dur">Длительность</label>
                <select
                  id="dur"
                  value={form.duration_min}
                  onChange={(e) =>
                    setForm({ ...form, duration_min: Number(e.target.value) })
                  }
                >
                  {DURATIONS.map((d) => (
                    <option key={d} value={d}>
                      {d} мин
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="tz">Часовой пояс</label>
                <select
                  id="tz"
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                >
                  {[...new Set([form.timezone, ...TIMEZONES])].map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field">
              <label htmlFor="agenda">Повестка (необязательно)</label>
              <textarea
                id="agenda"
                value={form.agenda}
                placeholder="Что обсуждаем и к чему хотим прийти"
                onChange={(e) => setForm({ ...form, agenda: e.target.value })}
              />
            </div>

            <div className="grid-2">
              <div className="field">
                <label htmlFor="rec">Запись</label>
                <select
                  id="rec"
                  value={form.auto_recording}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      auto_recording: e.target
                        .value as MeetingInput["auto_recording"],
                    })
                  }
                >
                  <option value="cloud">
                    В облако автоматически — нужна для разбора
                  </option>
                  <option value="local">Локально на компьютер ведущего</option>
                  <option value="none">Не записывать</option>
                </select>
                {form.auto_recording !== "cloud" && (
                  <div className="small muted" style={{ marginTop: 6 }}>
                    Без облачной записи сервис не сможет забрать файл и сделать
                    расшифровку с выжимкой.
                  </div>
                )}
              </div>
              <div className="field">
                <label htmlFor="host">Ведущий (email в Zoom)</label>
                <input
                  id="host"
                  value={form.host_email}
                  placeholder={status?.zoom.hostEmail ?? "me"}
                  onChange={(e) => setForm({ ...form, host_email: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Кого зовём</h3>
            <span className="muted small">
              выбрано: {form.participant_ids.length}
            </span>
          </div>
          <div className="card-body">
            {selected.length > 0 && (
              <div className="row" style={{ marginBottom: 12 }}>
                {selected.map((p) => (
                  <span
                    key={p.id}
                    className={`badge ${p.kind === "external" ? "amber" : "blue"}`}
                  >
                    {p.name}
                    <button
                      type="button"
                      className="ghost sm"
                      style={{ padding: "0 2px" }}
                      onClick={() => toggle(p.id)}
                      aria-label={`Убрать ${p.name}`}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="field">
              <input
                placeholder="Поиск по имени, почте, роли или компании"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {people.length === 0 ? (
              <div className="empty">
                <p>Список людей пуст.</p>
                <Link className="btn" to="/people">
                  Добавить участников
                </Link>
              </div>
            ) : (
              <div className="picker">
                {filtered.map((p) => (
                  <label className="picker-item" key={p.id}>
                    <input
                      type="checkbox"
                      checked={form.participant_ids.includes(p.id)}
                      onChange={() => toggle(p.id)}
                    />
                    <span
                      className={`avatar ${p.kind === "external" ? "external" : ""}`}
                    >
                      {initials(p.name)}
                    </span>
                    <span style={{ flex: 1 }}>
                      <strong>{p.name}</strong>
                      <span className="muted small"> · {p.email}</span>
                      {p.role && <div className="muted small">{p.role}</div>}
                    </span>
                    {p.kind === "external" && (
                      <span className="badge amber">внешний</span>
                    )}
                  </label>
                ))}
                {filtered.length === 0 && (
                  <div className="empty small">Никого не нашлось</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="row" style={{ marginTop: 16 }}>
          <button className="primary" type="submit" disabled={saving}>
            {saving && <span className="spinner" />}{" "}
            {editing ? "Сохранить" : "Создать встречу"}
          </button>
          <Link className="btn" to={editing ? `/meetings/${id}` : "/meetings"}>
            Отмена
          </Link>
        </div>
      </form>
    </>
  );
}
